import { useCallback, useEffect, useState } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Card, CardContent, styled } from '@mui/material';
import {
  TripLeg,
  TripLegStatusLogEntry,
} from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import dateUtils from '../../../services/DateUtils';
import findLastIndex from 'lodash/findLastIndex';

const PREFIX = 'TripLegStatusStepper';

const classes = {
  cardRoot: `${PREFIX}-card-root`,
  time: `${PREFIX}-time`,
};
const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.cardRoot}`]: {
    padding: 0,
    ['& .MuiCardContent-root']: {
      paddingTop: theme.spacing(6),
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      paddingBottom: theme.spacing(4),
    },
  },
  [`& .${classes.time}`]: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
}));

interface TripStep {
  label: string;
  completed?: string;
}

const TripLegStatusStepper = ({
  tripLeg,
  statusLogs,
}: {
  tripLeg: TripLeg;
  statusLogs: TripLegStatusLogEntry[];
}) => {
  const { t } = useTranslation('common');

  const [tripSteps, setTripSteps] = useState<TripStep[]>();
  const [activeStep, setActiveStep] = useState(-1);

  const getStepCompletedTime = useCallback(
    (status: string, timeZone: string) => {
      const ucStatus = status.toLocaleUpperCase();

      const idx = findLastIndex(
        statusLogs,
        (log: TripLegStatusLogEntry) => log.tripLegStatusBC === ucStatus,
      );
      if (idx >= 0) {
        const dt = DateTime.fromISO(statusLogs[idx].createdOnUTC).setZone(
          timeZone,
        );
        return dateUtils.formatTime(dt);
      }
      return undefined;
    },
    [statusLogs],
  );

  useEffect(() => {
    const steps: TripStep[] = [];
    const tz = tripLeg?.pickUpAddress?.timeZone;
    if (!tz) {
      return;
    }

    steps.push({
      label: t('tripStatusTrack.statusAssigned'),
      completed: getStepCompletedTime(TripLegStatus.Confirmed, tz),
    });
    steps.push({
      label: t('tripStatusTrack.statusEnroute'),
      completed: getStepCompletedTime(TripLegStatus.InTransit, tz),
    });
    steps.push({
      label: t('tripStatusTrack.onSite'),
      completed: getStepCompletedTime(TripLegStatus.Onsite, tz),
    });

    if (tripLeg?.status === TripLegStatus.NoLoad) {
      steps.push({
        label: t('tripStatusTrack.statusNoLoad'),
        completed: getStepCompletedTime(TripLegStatus.NoLoad, tz),
      });
    } else {
      steps.push({
        label: t('tripStatusTrack.statusPickup'),
        completed: getStepCompletedTime(TripLegStatus.Onboard, tz),
      });
      steps.push({
        label: t('tripStatusTrack.statusDropOff'),
        completed: getStepCompletedTime(TripLegStatus.Completed, tz),
      });
    }

    setTripSteps(steps);

    switch (tripLeg?.status) {
      case TripLegStatus.Confirmed:
        setActiveStep(1);
        break;
      case TripLegStatus.InTransit:
        setActiveStep(2);
        break;
      case TripLegStatus.Onsite:
        setActiveStep(3);
        break;
      case TripLegStatus.NoLoad:
        setActiveStep(4);
        break;
      case TripLegStatus.Onboard:
        setActiveStep(4);
        break;
      case TripLegStatus.Completed:
        setActiveStep(5);
        break;
      default:
        setActiveStep(-1);
        break;
    }
  }, [tripLeg, getStepCompletedTime]);

  return (
    <StyledCard className={classes.cardRoot}>
      <CardContent>
        <Stepper
          className="no-padding auto-connector-width"
          activeStep={activeStep}
          alternativeLabel={true}>
          {tripSteps?.map((step) => (
            <Step key={step.label}>
              <StepLabel>
                <div>{step.label}</div>
                <div className={classes.time}>{step.completed}</div>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </CardContent>
    </StyledCard>
  );
};

export default TripLegStatusStepper;
